import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { SupportService } from '@/app/services/support/support.service';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import {
  SupportAlertPreferences,
  SupportAttachment,
  SupportInboundTicketCategory,
  SupportTicketDetail,
} from '@/app/common/general-types/support-ticket';
import { isUkSupportCountry } from '@/app/common/support-chat.util';
import { SUPPORT_TICKET_CATEGORY_OPTIONS } from '@/app/common/support-ticket-category.util';
import { SupportAttachmentInputComponent } from '@/app/molecules/support-attachment-input/support-attachment-input.component';
import { SupportAlertPreferencesInputComponent } from '@/app/molecules/support-alert-preferences-input/support-alert-preferences-input.component';

@Component({
  selector: 'app-support-new-ticket',
  imports: [
    FormsModule,
    RouterLink,
    SupportAttachmentInputComponent,
    SupportAlertPreferencesInputComponent,
  ],
  templateUrl: './support-new-ticket.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportNewTicketComponent implements OnInit, OnDestroy {
  private readonly supportService = inject(SupportService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  subscriptionList = new Subscription();

  readonly categoryOptions = SUPPORT_TICKET_CATEGORY_OPTIONS;

  category: SupportInboundTicketCategory | '' = '';
  subject = '';
  message = '';
  attachments: SupportAttachment[] = [];
  alertPreferences: SupportAlertPreferences = {
    sms: true,
    whatsapp: false,
    email: false,
    push: false,
  };
  readonly submitting = signal(false);
  readonly error = signal('');
  readonly hasPushDevice = signal(false);

  ngOnInit(): void {
    const user = this.currentUserService.shoutUser;
    const countryCode =
      user?.countryCode ?? this.currentUserService.focussedBrand?.countryCode;
    const isUk = isUkSupportCountry(countryCode);
    const hasMobile = !!user?.mobileNumber?.trim();

    this.alertPreferences = {
      sms: hasMobile && !isUk,
      whatsapp: hasMobile && isUk,
      email: false,
      push: false,
    };

    this.loadPushAvailability();
  }

  /**
   * Push needs a device registered by one of the mobile apps, so we can't tell from the browser
   * alone. Defaults on once we know there is one — it's the channel they're most likely to see.
   */
  private loadPushAvailability(): void {
    this.subscriptionList.add(
      this.supportService.getPushAvailability().subscribe({
        next: ({ hasPushDevice }) => {
          this.hasPushDevice.set(hasPushDevice);
          if (hasPushDevice) {
            this.alertPreferences = { ...this.alertPreferences, push: true };
          }
          this.cdr.detectChanges();
        },
        error: () => this.hasPushDevice.set(false),
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  onAttachmentsChange(attachments: SupportAttachment[]): void {
    this.attachments = attachments;
  }

  onAlertPreferencesChange(prefs: SupportAlertPreferences): void {
    this.alertPreferences = prefs;
  }

  hasAlertPreference(): boolean {
    return (
      this.alertPreferences.sms ||
      this.alertPreferences.whatsapp ||
      this.alertPreferences.email ||
      this.alertPreferences.push
    );
  }

  submit(): void {
    this.error.set('');
    const subject = this.subject.trim();
    const message = this.message.trim();

    if (!this.category) {
      this.error.set('Please select a category.');
      return;
    }

    if (!subject || (!message && this.attachments.length === 0)) {
      this.error.set('Please enter a subject and message or attach a file.');
      return;
    }

    if (!this.hasAlertPreference()) {
      this.error.set('Select at least one way to be notified about replies.');
      return;
    }

    this.submitting.set(true);
    this.subscriptionList.add(
      this.supportService
        .createTicket({
          category: this.category,
          subject,
          message,
          businessAccountId: this.currentUserService.focussedBrand?.id,
          attachments: this.attachments.length ? this.attachments : undefined,
          alertPreferences: this.alertPreferences,
          appOs: 'web',
        })
        .subscribe({
          next: (ticket: SupportTicketDetail) => {
            this.submitting.set(false);
            this.router.navigate(['/support/tickets', ticket.id], {
              queryParams: { submitted: '1' },
            });
          },
          error: () => {
            this.submitting.set(false);
            this.error.set('Could not create your ticket. Please try again.');
            this.cdr.markForCheck();
          },
        }),
    );
  }
}
